import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Loader2, Calendar } from "lucide-react";
import Seo from "../components/Seo";
import { businessSchema, websiteSchema } from "../lib/seoSchemas";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function buildExcerpt(post) {
  const raw = (post.excerpt || "").trim();
  if (raw.length >= 30) return raw.slice(0, 200);
  return raw;
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/blog`)
      .then((res) => {
        if (!cancelled) setPosts(res.data?.posts || []);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div data-testid="page-blog">
      <Seo
        title="Field Notes | Holder Hospitality Consulting"
        description="Operator-perspective writing on fractional leadership, restaurant turnarounds, and the people side of multi-unit hospitality operations."
        schemas={[businessSchema, websiteSchema]}
      />

      <section className="bg-white py-20 lg:py-28 border-b border-ink-line" data-testid="blog-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6">Field Notes</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-brand-700 leading-[1.02] tracking-tight max-w-4xl">
            Writing from inside the building.
          </h1>
          <p className="mt-8 text-lg text-ink-soft max-w-2xl leading-relaxed">
            Notes on fractional leadership, restaurant turnarounds, and the people side of
            multi-unit hospitality. Written by Caleb, not a content team.
          </p>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="blog-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <div className="py-16 flex items-center justify-center text-ink-muted">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading
            </div>
          ) : posts.length === 0 ? (
            <div className="py-16 text-center" data-testid="blog-empty">
              <div className="font-serif text-3xl text-brand-700">
                The first post is on its way.
              </div>
              <p className="mt-3 text-ink-muted">
                In the meantime, you can put thirty minutes on the calendar to talk
                operations directly.
              </p>
            </div>
          ) : (
            <ul className="border-t border-ink-line">
              {posts.map((p) => (
                <li
                  key={p.id}
                  className="border-b border-ink-line py-10 lg:py-12 group"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <Link
                    to={`/blog/${p.slug}`}
                    className="block grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 hover:bg-brand-50/40 transition-colors -mx-4 px-4 py-2 rounded"
                  >
                    <div className="lg:col-span-3 flex items-start gap-2 text-sm text-ink-muted">
                      <Calendar className="w-4 h-4 mt-0.5 text-brand-400 shrink-0" />
                      <time dateTime={p.publishedAt}>{formatDate(p.publishedAt)}</time>
                    </div>
                    <div className="lg:col-span-9">
                      <h2 className="font-serif text-3xl sm:text-4xl text-brand-700 leading-tight group-hover:text-brand-800 transition-colors">
                        {p.title}
                      </h2>
                      {buildExcerpt(p) && (
                        <p className="mt-4 text-ink-soft leading-relaxed line-clamp-3">
                          {buildExcerpt(p)}
                        </p>
                      )}
                      <span
                        className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-brand-700"
                        data-testid={`blog-read-more-${p.slug}`}
                      >
                        Read more
                        <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
